import React from 'react'
import format from 'date-fns/format'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'

import AttachFileIcon from '@mui/icons-material/AttachFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ImageIcon from '@mui/icons-material/Image'
import PersonIcon from '@mui/icons-material/Person'

import ScrollableBoxWrapper from 'components/styled/ScrollableBox'
import DocumentHTMLContent from 'pages/html/DocumentHTMLContent'
import { useDocument } from 'hooks/useDocuments'
import isEmptyObject from 'helpers/node/isEmptyObject'
import useNotification from 'hooks/context/useNotification'

function NotificationPreview({ documentId, disableScroll = false }) {
  const {
    downloadDocumentById,
    document: notification,
    loading,
  } = useDocument(documentId, {
    embed:
      'notification|notification_attachments|notes|metadata|linked_notification_documents|return_data',
  })

  const { setError } = useNotification()

  const formatDate = (dateString) => {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)

      // Format: "Mon, Nov 3, 3:46 PM"
      const dateFormatted = format(date, 'EEE, MMM d, h:mm a')

      // Get relative time: "21 hours ago"
      const relativeTime = formatDistanceToNow(date, { addSuffix: true })

      // Combine: "Mon, Nov 3, 3:46 PM (21 hours ago)"
      return `${dateFormatted} (${relativeTime})`
    } catch (err) {
      return dateString
    }
  }

  const getAttachmentIcon = (type) => {
    if (
      type === 'image' ||
      type === 'png' ||
      type === 'jpg' ||
      type === 'jpeg'
    ) {
      return <ImageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
    }
    if (type === 'pdf') {
      return <AttachFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
    }
    return <InsertDriveFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
  }

  const sender = notification?._embedded?.notification?.from
  const subject = notification?._embedded?.notification?.subject
  const date =
    notification?._embedded?.notification?.datetime_sent ||
    notification?._embedded?.notification?.datetime_created
  const attachments = notification?._embedded?.notification?.attachments ?? {}

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
          borderBottom: '1px solid',
          borderColor: 'lightgray.main',
          pb: 4,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          <Avatar
            sx={{
              bgcolor: 'grey.300',
              width: 40,
              height: 40,
            }}
          >
            <PersonIcon sx={{ color: 'grey.600' }} />
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                {sender?.name || 'Unknown Sender'}
              </Typography>
              <Typography my="auto" variant="body2" color="text.secondary">
                {`<${sender?.address || ''}>`}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              to{' '}
              {notification?._embedded?.notification?.to?.address ||
                'Unknown Recipient'}
            </Typography>
          </Box>
        </Box>
        <Typography mt="auto" variant="body2" color="text.secondary">
          {formatDate(date)}
        </Typography>
      </Box>

      {/* Subject Line */}
      <Typography variant="h2" fontWeight="bold" sx={{ mb: 3 }}>
        {subject || 'No Subject'}
      </Typography>

      {/* Body Text */}
      <ScrollableBoxWrapper
        isPerfectScrollbar={!disableScroll}
        sx={{
          height: disableScroll ? '100%' : '60vh',
          overflow: disableScroll ? 'visible' : 'auto',
        }}
      >
        <DocumentHTMLContent document={notification} />
      </ScrollableBoxWrapper>

      {/* Attachments Section */}
      {!isEmptyObject(attachments) && (
        <Box sx={{ pt: 4 }}>
          <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
            Attachments
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="nowrap"
            sx={{ overflowX: 'auto', pb: 1 }}
          >
            {Object.entries(attachments).map(
              ([attachmentDocId, attachmentFilename]) => {
                const fileExtension = attachmentFilename
                  ?.split('.')
                  ?.pop()
                  ?.toLowerCase()

                return (
                  <Chip
                    key={attachmentDocId}
                    icon={getAttachmentIcon(fileExtension)}
                    label={attachmentFilename}
                    clickable
                    sx={{
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      backgroundColor: 'grey.50',
                      '& .MuiChip-icon': {
                        marginLeft: 1.5,
                        marginRight: 0,
                      },
                      '& .MuiChip-label': {
                        paddingLeft: 1,
                        paddingRight: 1.5,
                      },
                    }}
                    variant="outlined"
                    onClick={() => {
                      if (
                        !attachmentDocId ||
                        attachmentDocId.startsWith('inactive_')
                      ) {
                        setError(
                          'This document cannot be downloaded as it is inactive.'
                        )
                        return
                      }

                      downloadDocumentById(attachmentDocId).catch((error) => {
                        setError('Could not download attachment.', error)
                      })
                    }}
                  />
                )
              }
            )}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default NotificationPreview
